import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import Header from "../../../../components/Header";
import BottomNavBar from "../../../../components/BottomNavBar";
import {
    ProductContainer,
    ProductGrid,
    ProductCard,
    ProductMeta,
    SectionTitle,
    CategoryContainer,
    ProductIcon,
    ProductDescription,
    ProductTitle,
    EmptyState,
    SearchContainer,
    SearchInput,
    LoadingSpinner,
    CategoryHeader
} from './styled';
import { ROUTE_PATH, ROUTE_PRODUCT_PATH } from '../../../../constants/routePath';
import MyStoreTabs from '../../../../components/MyStoreTabs';
import urlImage from '../../../../assets/images/url-product-image.webp';
import digitalProduct from '../../../../assets/icons/digital-icon.jpg';
import customProduct from '../../../../assets/icons/custom-product-image.jpg';
import emailProduct from '../../../../assets/icons/email-icon.jpg';
import videoProduct from '../../../../assets/icons/video-icon.jpg';
import affiliateProduct from '../../../../assets/images/sparkle-black-logo.jpg';
import chatWithMe from '../../../../assets/icons/chat-icon.jpg';
import SparkleShop from '../../../../assets/icons/shop-icon.jpg';
import SpotifyFilled from '../../../../assets/icons/spotify-icon.jpg';
import AppleMusicFilled from '../../../../assets/images/apple/apple-logo.jpg';
import tipIcon from '../../../../assets/icons/tip-jar.jpg';
import imageIcon from '../../../../assets/icons/image-icon.jpg';
import engagementProduct from '../../../../assets/icons/engagement-icon.jpg';

const Products = () => {
    const [activeTab, setActiveTab] = useState('products');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Simulate loading time for products
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    const handleTabChange = (key) => {
        setActiveTab(key);
    };

    const productCategories = [
        {
            title: "Links & Media",
            items: [
                {
                    path: ROUTE_PRODUCT_PATH.URL_PRODUCT,
                    icon: urlImage,
                    title: "URL",
                    description: "Redirect users to an external site or add a Creator's affiliate link",
                    tag: "Popular"
                },
                {
                    path: ROUTE_PRODUCT_PATH.IMAGE_PRODUCT,
                    icon: imageIcon,
                    title: "Image Gallery",
                    description: "Showcase multiple images to engage your audience",
                },
                {
                    path: ROUTE_PRODUCT_PATH.VIDEO_PRODUCT,
                    icon: videoProduct,
                    title: "Display Your Videos",
                    description: "Share and showcase your video content with your audience"
                }
            ]
        },
        {
            title: "Digital Content",
            items: [
                {
                    path: ROUTE_PRODUCT_PATH.DIGITAL_PRODUCT,
                    icon: digitalProduct,
                    title: "Digital Products",
                    description: "PDFs, Guides, Templates, Exclusive Content, eBooks, etc.",
                    tag: "Pouplar"
                },
                {
                    path: ROUTE_PRODUCT_PATH.SPARKLE_CONNECT,
                    icon: chatWithMe,
                    title: "Chat with Me",
                    description: "Host one-on-one video calls for coaching, or casual convos."
                },
                {
                    path: ROUTE_PRODUCT_PATH.CUSTOM_PRODUCT,
                    icon: customProduct,
                    title: "Custom Product",
                    description: "Create and sell unique digital products to your audience"
                },
                {
                    path: ROUTE_PRODUCT_PATH.ENGAGEMENT,      
                    icon: engagementProduct,                    
                    title: "Monetize Engagement",                     
                    description: "Let your audience purchase Shoutouts, Follows, Likes, and Comments", 
                }
            ]
        },
        {
            title: "Music & Streaming",
            items: [
                {
                    path: ROUTE_PRODUCT_PATH.SPOTIFY,
                    icon: SpotifyFilled,
                    title: "Add Spotify",
                    description: "Embed your Spotify playlist, let users listen, etc.",
                },
                {
                    path: ROUTE_PRODUCT_PATH.APPLE_MUSIC,
                    icon: AppleMusicFilled,
                    title: "Add Apple Music",
                    description: "Embed your Apple Music playlist so fans can discover your tracks.",
                }
            ]
        },
        // {
        //     title: "Services Management",
        //     items: [
        //         {
        //             path: ROUTE_PRODUCT_PATH.APPOINTMENTS,
        //             icon: emailProduct, 
        //             title: "Appointments",
        //             description: "Book services with trusted professionals. Perfect for stylists, barbers, trainers, and other service providers looking to manage appointments efficiently."
        //         }
        //     ]
        // },
        {
            title: "Audience Engagement",
            items: [
                {
                    path: ROUTE_PRODUCT_PATH.EMAIL_PRODUCT,
                    icon: emailProduct,
                    title: "Collect Emails",
                    description: "Capture Audience Info with a Lead Magnet",
                }
            ]
        },
        {
            title: "Monetization",
            items: [
                {
                    path: ROUTE_PRODUCT_PATH.TIP_PRODUCT,
                    icon: tipIcon,
                    title: "Tip Jar",
                    description: "Encourage your supporters to leave a tip and help you keep creating!",
                    tag: "New"
                },
                {
                    path: ROUTE_PATH.CREATOR_STORE,
                    icon: SparkleShop,
                    title: "Your Shop",
                    description: "Showcase and sell your unique products to your audience"
                },
                {
                    path: ROUTE_PRODUCT_PATH.REFERRAL_PRODUCT,
                    icon: affiliateProduct,
                    title: "Sparkle Affiliate Program",
                    description: "Invite a friend and earn 20% of their subscription every month!"
                }
            ]
        }
    ];

    const filteredCategories = productCategories.map(category => ({
        ...category,
        items: category.items.filter(item =>
            item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
    })).filter(category => category.items.length > 0);

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.3,
                ease: "easeOut"
            }
        },
        exit: {
            opacity: 0,
            y: -20,
            transition: {
                duration: 0.2
            }
        }
    };

    return (
        <div>
            <Header />
            <MyStoreTabs activeTab={activeTab} onTabChange={handleTabChange} />
            <ProductContainer>
                <SearchContainer>
                    <SearchInput
                        prefix={<SearchOutlined />}
                        placeholder="Search products..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        allowClear
                    />
                </SearchContainer>

                {isLoading ? (
                    <LoadingSpinner />
                ) : filteredCategories.length > 0 ? (
                    <AnimatePresence>
                        {filteredCategories.map((category, categoryIndex) => (
                            <CategoryContainer
                                key={category.title}
                                as={motion.div}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ delay: categoryIndex * 0.1 }}
                            >
                                <CategoryHeader>
                                    <SectionTitle>{category.title}</SectionTitle>
                                </CategoryHeader>
                                <ProductGrid>
                                    {category.items.map((item, index) => (
                                        <motion.div
                                            key={item.title}
                                            variants={cardVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                            layout
                                        >
                                            <Link to={item.path}>
                                                <ProductCard
                                                    hoverable
                                                    data-testid={`product-card-${categoryIndex}-${index}`}
                                                >
                                                    <ProductMeta
                                                        avatar={<ProductIcon size={64} src={item.icon} />}
                                                        title={
                                                            <ProductTitle>
                                                                {item.title}
                                                                {item.tag && (
                                                                    <span className="product-tag">{item.tag}</span>
                                                                )}
                                                            </ProductTitle>
                                                        }
                                                        description={
                                                            <ProductDescription>{item.description}</ProductDescription>
                                                        }
                                                    />
                                                </ProductCard>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </ProductGrid>
                            </CategoryContainer>
                        ))}
                    </AnimatePresence>
                ) : (
                    <EmptyState>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            <h3>No products found</h3>
                            <p>Try adjusting your search terms</p>
                        </motion.div>
                    </EmptyState>
                )}
            </ProductContainer>
            <BottomNavBar />
        </div>
    );
};

export default Products;